import type { StatBlock, StatId } from './stats.ts';
import { statModForScore } from '../rules/statModifiers.ts';

/** A pet is linked to a crawler but is explicitly not itself a crawler. */
export type PetOrigin = 'earth-born' | 'dungeon-born';
export type PetAttitude = 'hostile' | 'calm' | 'friendly' | 'bonded';
export type PetRole = 'tank' | 'aggressive' | 'utility';

export type PetSpecialAbilityId =
  | 'move-60'
  | 'taunt'
  | 'catcher'
  | 'swim-30'
  | 'burrow-30'
  | 'flight-50'
  | 'jump-50'
  | 'heightened-senses'
  | 'resistance-uncommon';

/** Level-15 maturity benefits printed as examples, plus a custom escape hatch. */
export type PetMatureBenefitId = 'earthquake' | 'wrecking-ball' | 'spells' | 'regeneration' | 'custom';

export interface PetMatureBenefit {
  id: PetMatureBenefitId;
  /** GM-authored details, especially for the intentionally open-ended custom option. */
  notes: string;
  /** Preserved display name when a GM-defined custom option is selected. */
  customName?: string;
  /** Stable option ID when this came from a GM-defined custom maturity option. */
  customOptionId?: string;
}

export interface PetCustomMatureBenefitOption {
  id: string;
  name: string;
  notes: string;
}

export interface PetCompanion {
  id: string;
  name: string;
  species: string;
  origin: PetOrigin;
  /** Earth-born pets may have a crawler ID even though they are not crawlers. */
  crawlerNumber: string | null;
  level: number;
  size: number;
  attitude: PetAttitude;
  /** Chosen once Bonded. Null while the animal is not Bonded. */
  role: PetRole | null;
  unenhancedStats: StatBlock;
  healthBarSlotsRemaining: number;
  specialAbilities: PetSpecialAbilityId[];
  /** Human-readable detail when resistance-uncommon is selected. */
  resistanceDamageType: string;

  /** GM-awarded but not yet allocated pet Stat Points. Intelligence may not receive these points. */
  unspentStatPoints: number;
  /** Owning crawler Level through which this pet has already received its matching level-up event. */
  ownerLevelLastAdvanced: number;
  /** Pet Mana currently available. Max Mana is derived from Intelligence + manaBonus. */
  currentMana: number;
  /** Explicit exception capacity from a benefit, item, or GM-authored rule. */
  manaBonus: number;
  /** Stable non-Attack, non-Spell Skill IDs granted to the pet. Their Rank is always the current Floor. */
  skillIds: string[];
  /** Stable Spell IDs assigned by the GM. */
  spellIds: string[];
  /** Optional GM Rank override by Spell ID. Unlisted Spells default to the current Floor. */
  spellRanks: Record<string, number>;
  /** Level-15 maturity benefit. Null before selection, including at Level 15 while pending. */
  matureBenefit: PetMatureBenefit | null;
  /** GM-authored Level-15 choices that the player may select during a pet level-up. */
  customMatureBenefits: PetCustomMatureBenefitOption[];
  /** Owner-inventory Item IDs assigned to the pet. Inventory ownership remains with the crawler. */
  gearItemIds: string[];

  notes: string;
  createdAt: string;
  updatedAt: string;
}

export const PET_STARTING_STATS: StatBlock = { str: 2, int: 1, con: 2, dex: 2, cha: 1 };
export const PET_SIZE_VALUES: readonly number[] = [1, 2, 3, 4, 5];

export const PET_SPECIAL_ABILITY_LABELS: Record<PetSpecialAbilityId, string> = {
  'move-60': 'Move 60ft',
  taunt: 'Taunt Skill (Rank = Floor)',
  catcher: 'Catcher Skill (Rank = Floor)',
  'swim-30': 'Swim 30ft',
  'burrow-30': 'Burrow 30ft',
  'flight-50': 'Flight 50ft',
  'jump-50': 'Jump 50ft',
  'heightened-senses': 'Heightened Senses',
  'resistance-uncommon': 'Resistance to an uncommon damage type',
};

export const PET_MATURE_BENEFIT_LABELS: Record<PetMatureBenefitId, string> = {
  earthquake: 'Earthquake',
  'wrecking-ball': 'Wrecking Ball',
  spells: 'Spells',
  regeneration: 'Regeneration',
  custom: 'Custom maturity benefit',
};

export function petMatureBenefitLabel(benefit: PetMatureBenefit): string {
  return benefit.id === 'custom' && benefit.customName?.trim() ? benefit.customName.trim() : PET_MATURE_BENEFIT_LABELS[benefit.id];
}

export function petMaxHealthSlots(pet: Pick<PetCompanion, 'size' | 'level'>): number {
  return Math.min(10, pet.size + Math.max(0, pet.level - 1));
}

export function petEvadeModifier(pet: Pick<PetCompanion, 'unenhancedStats'>): number {
  return statModForScore(pet.unenhancedStats.dex);
}

export function petDamageResistance(pet: Pick<PetCompanion, 'role'>, floor: number): number {
  return pet.role === 'tank' ? floor : 0;
}

export function petFloorRank(floor: number): number {
  return floor;
}

/** General Mana rule: Max Mana is Intelligence, plus explicit pet-specific exceptions. */
export function petMaxMana(pet: Pick<PetCompanion, 'unenhancedStats' | 'manaBonus'>): number {
  return Math.max(0, pet.unenhancedStats.int + pet.manaBonus);
}

/** Pets gain two levels per crawler level until reaching 15, then one at a time. */
export function petLevelsForCrawlerLevelUp(level: number): number {
  if (!Number.isInteger(level) || level < 1) return 0;
  if (level < 15) return Math.min(2, 15 - level);
  return 1;
}

export function petCanReceiveAdvancementPoint(stat: StatId): boolean {
  return stat !== 'int';
}

export function createPetCompanion(input: {
  id: string;
  name: string;
  species: string;
  origin: PetOrigin;
  crawlerNumber?: string | null;
  size?: number;
  /** Current owner Level when the pet joins; prevents retroactive level-up credits. */
  ownerLevel?: number;
  now: string;
}): PetCompanion {
  const size = input.size ?? 3;
  return {
    id: input.id,
    name: input.name.trim(),
    species: input.species.trim(),
    origin: input.origin,
    crawlerNumber: input.origin === 'earth-born' ? (input.crawlerNumber?.trim() || null) : null,
    level: 1,
    size,
    attitude: input.origin === 'earth-born' ? 'bonded' : 'calm',
    role: input.origin === 'earth-born' ? 'utility' : null,
    unenhancedStats: { ...PET_STARTING_STATS },
    healthBarSlotsRemaining: Math.min(10, size),
    specialAbilities: input.origin === 'earth-born' ? ['heightened-senses', 'move-60'] : [],
    resistanceDamageType: '',
    unspentStatPoints: 0,
    ownerLevelLastAdvanced: Number.isInteger(input.ownerLevel) && (input.ownerLevel ?? 0) >= 1 ? Math.floor(input.ownerLevel!) : 1,
    currentMana: PET_STARTING_STATS.int,
    manaBonus: 0,
    skillIds: [],
    spellIds: [],
    spellRanks: {},
    matureBenefit: null,
    customMatureBenefits: [],
    gearItemIds: [],
    notes: '',
    createdAt: input.now,
    updatedAt: input.now,
  };
}

export function validatePetCompanion(pet: PetCompanion): string[] {
  const errors: string[] = [];
  if (!pet.id) errors.push('Pet is missing an id.');
  if (!pet.name.trim()) errors.push('Pet name is required.');
  if (!pet.species.trim()) errors.push('Pet species/type is required.');
  if (pet.origin !== 'earth-born' && pet.origin !== 'dungeon-born') errors.push('Pet origin is invalid.');
  if (!Number.isInteger(pet.level) || pet.level < 1) errors.push('Pet level must be a whole number of at least 1.');
  if (!PET_SIZE_VALUES.includes(pet.size)) errors.push('Pet size must be between Tiny (1) and Large (5).');
  for (const [stat, value] of Object.entries(pet.unenhancedStats)) {
    if (!Number.isInteger(value) || value < 1) errors.push(`Pet ${stat.toUpperCase()} must be a whole number of at least 1.`);
  }
  if (pet.origin === 'earth-born' && pet.unenhancedStats.int !== 1) {
    errors.push('An Earth-born animal with Intelligence 2+ is a crawler, not a pet.');
  }
  const maxHealth = petMaxHealthSlots(pet);
  if (!Number.isInteger(pet.healthBarSlotsRemaining) || pet.healthBarSlotsRemaining < 0 || pet.healthBarSlotsRemaining > maxHealth) {
    errors.push(`Pet Health Bar must be between 0 and ${maxHealth} slots.`);
  }
  if (pet.attitude === 'bonded') {
    if (pet.role === null) errors.push('A Bonded pet must have a role.');
  } else if (pet.role !== null) {
    errors.push('Only a Bonded pet may have a pet role.');
  }
  if (pet.role === 'aggressive' && pet.specialAbilities.length !== 0) errors.push('An Aggressive pet chooses no Special Abilities.');
  if (pet.role === 'tank' && pet.specialAbilities.length !== 1) errors.push('A Tank pet chooses exactly one Special Ability.');
  if (pet.role === 'utility' && pet.specialAbilities.length !== 2) errors.push('A Utility pet chooses exactly two Special Abilities.');
  if (new Set(pet.specialAbilities).size !== pet.specialAbilities.length) errors.push('Pet Special Abilities cannot be duplicated.');
  for (const ability of pet.specialAbilities) {
    if (!(ability in PET_SPECIAL_ABILITY_LABELS)) errors.push(`Unknown pet Special Ability: ${ability}.`);
  }
  if (!Number.isInteger(pet.unspentStatPoints) || pet.unspentStatPoints < 0) errors.push('Pet unspent Stat Points must be a whole number of at least 0.');
  if (!Number.isInteger(pet.ownerLevelLastAdvanced) || pet.ownerLevelLastAdvanced < 1) errors.push('Pet owner Level advancement anchor must be a whole number of at least 1.');
  if (!Number.isInteger(pet.manaBonus) || pet.manaBonus < 0) errors.push('Pet Mana bonus must be a whole number of at least 0.');
  const maxMana = petMaxMana(pet);
  if (!Number.isInteger(pet.currentMana) || pet.currentMana < 0 || ((!Array.isArray(pet.gearItemIds) || pet.gearItemIds.length === 0) && pet.currentMana > maxMana)) errors.push(`Pet current Mana must be between 0 and ${maxMana}.`);
  if (!Array.isArray(pet.skillIds) || pet.skillIds.some((id) => typeof id !== 'string' || id.trim().length === 0)) errors.push('Pet Skill IDs must be non-empty text values.');
  else if (new Set(pet.skillIds).size !== pet.skillIds.length) errors.push('Pet Skills cannot be duplicated.');
  if (!Array.isArray(pet.spellIds) || pet.spellIds.some((id) => typeof id !== 'string' || id.trim().length === 0)) errors.push('Pet Spell IDs must be non-empty text values.');
  else if (new Set(pet.spellIds).size !== pet.spellIds.length) errors.push('Pet Spells cannot be duplicated.');
  if (typeof pet.spellRanks !== 'object' || pet.spellRanks === null || Array.isArray(pet.spellRanks)) errors.push('Pet Spell Ranks must be an object.');
  else {
    for (const [id, rank] of Object.entries(pet.spellRanks)) {
      if (!pet.spellIds.includes(id)) errors.push(`Pet Spell Rank override references unassigned Spell: ${id}.`);
      if (!Number.isInteger(rank) || rank < 1 || rank > 20) errors.push(`Pet Spell Rank for ${id} must be a whole number from 1 to 20.`);
    }
  }
  if (pet.matureBenefit !== null) {
    if (pet.level < 15) errors.push('A pet cannot receive a maturity benefit before Level 15.');
    else if (!(pet.matureBenefit.id in PET_MATURE_BENEFIT_LABELS)) errors.push('Pet maturity benefit is invalid.');
    else if (typeof pet.matureBenefit.notes !== 'string') errors.push('Pet maturity benefit notes must be text.');
    if (pet.matureBenefit.customName !== undefined && typeof pet.matureBenefit.customName !== 'string') errors.push('Pet custom maturity benefit name must be text.');
    if (pet.matureBenefit.customOptionId !== undefined && typeof pet.matureBenefit.customOptionId !== 'string') errors.push('Pet custom maturity benefit option ID must be text.');
  }
  if (!Array.isArray(pet.customMatureBenefits)) errors.push('Pet custom maturity benefit options must be a list.');
  else {
    const ids = new Set<string>();
    for (const option of pet.customMatureBenefits) {
      if (!option || typeof option !== 'object') { errors.push('Pet custom maturity benefit option is invalid.'); continue; }
      if (typeof option.id !== 'string' || !option.id.trim()) errors.push('Pet custom maturity benefit option ID is required.');
      else if (ids.has(option.id)) errors.push(`Pet custom maturity benefit option ID is duplicated: ${option.id}.`);
      else ids.add(option.id);
      if (typeof option.name !== 'string' || !option.name.trim()) errors.push('Pet custom maturity benefit option name is required.');
      if (typeof option.notes !== 'string') errors.push('Pet custom maturity benefit option notes must be text.');
    }
  }
  if (!Array.isArray(pet.gearItemIds) || pet.gearItemIds.some((id) => typeof id !== 'string' || id.trim().length === 0)) errors.push('Pet gear references must be non-empty Item IDs.');
  return errors;
}
